import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import {
  Home,
  Disc3,
  Store,
  MessageCircle,
  User,
  Search,
  Settings,
  LogOut,
  Crown,
  Menu as MenuIcon,
  Sparkles,
  Users,
  Award,
  ChevronDown,
  UserPlus,
  ChevronRight,
  X,
  Activity,
  CalendarDays,
  CircleDotDashed,
} from "lucide-react";

import { useQuery } from "@tanstack/react-query";
import { useIdle } from "@/hooks/useIdle";
import { useSwipeBack } from "@/hooks/useSwipeBack";

import { useServerFn } from "@tanstack/react-start";
import { listConversations } from "@/lib/chat.functions";
import { useState, useRef, useEffect, lazy, Suspense, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import vinyIcon from "@/assets/viny-icon-2026.png.asset.json";

import { SiteFooter } from "./SiteFooter";
const CosmicSpectrum = lazy(() =>
  import("./ui/cosmos-spectrum").then((m) => ({ default: m.CosmicSpectrum })),
);
import { PremiumBadge } from "./PremiumBadge";

import { FramedAvatar } from "./FramedAvatar";

function NewBadgeLottie({ className }: { size?: number; scale?: number; className?: string }) {
  return (
    <span
      className={
        "inline-flex items-center rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary " +
        (className ?? "")
      }
    >
      novo
    </span>
  );
}

import { NotificationBell } from "./NotificationBell";
import { DesktopTopBar } from "./desktop/DesktopTopBar";
// Perf: o launcher usa framer-motion. Carregado sob demanda para não colocar a
// biblioteca de animação no caminho crítico (inclusive da landing page).
const AppLauncher = lazy(() => import("./AppLauncher").then((m) => ({ default: m.AppLauncher })));

import { Sheet, SheetContent, SheetClose } from "@/components/ui/sheet";
import { useCurrentUser, signOut } from "@/hooks/useCurrentUser";
import { useIsPremium } from "@/hooks/useIsPremium";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useMarketplaceEnabled } from "@/lib/i18n";

import { ProDrawer } from "@/components/ProDrawer";
import { InstallAppMenuCard } from "@/components/PwaInstallPrompt";
import { ChatDock } from "@/components/chat/ChatDock";

type NavItem = {
  to: string;
  label: string;
  shortLabel?: string;
  icon: typeof Home;
  isNew?: boolean;
};

const primaryNav: readonly NavItem[] = [
  { to: "/explore", label: "Explorar", icon: Home },
  { to: "/collection", label: "Minha coleção", shortLabel: "Coleção", icon: Disc3 },
] as const;

// Items agrupados sob o dropdown "Comunidade" no desktop
const communityNav: readonly NavItem[] = [
  { to: "/colecionadores", label: "Colecionadores", icon: Users },
  { to: "/comunidades", label: "Comunidades", icon: Users },
] as const;

const moreNav: readonly NavItem[] = [
  { to: "/ritual", label: "Viny Ritual", icon: CircleDotDashed, isNew: true },
  { to: "/marketplace", label: "Marketplace", icon: Store },
  { to: "/mini-site", label: "Mini Site", icon: Store },
] as const;

// Mobile navega como itens planos (sem dropdown)
const mobileExtraNav: readonly NavItem[] = [
  { to: "/chat", label: "Mensagens", icon: MessageCircle },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { profile, isAuthenticated, loading: authLoading } = useCurrentUser();
  const { isPremium } = useIsPremium();
  const { isAdmin } = useIsAdmin();
  const marketplaceEnabled = useMarketplaceEnabled();
  const navigate = useNavigate();
  useSwipeBack();
  const [menuOpen, setMenuOpen] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Unread messages indicator — chrome data, so it waits for the browser to be
  // idle instead of competing with the page's own content requests.
  const idle = useIdle();
  const fetchConvs = useServerFn(listConversations);
  const convsQ = useQuery({
    queryKey: ["conversations"],
    queryFn: () => fetchConvs(),
    enabled: isAuthenticated && idle,
    staleTime: 3 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: true,
  });

  const [lastSeenChat, setLastSeenChat] = useState<number>(0);
  useEffect(() => {
    try {
      const v = Number(localStorage.getItem("chat_last_seen") ?? 0);
      setLastSeenChat(Number.isFinite(v) ? v : 0);
    } catch {}
  }, []);
  useEffect(() => {
    if (pathname.startsWith("/chat")) {
      const now = Date.now();
      try {
        localStorage.setItem("chat_last_seen", String(now));
      } catch {}
      setLastSeenChat(now);
    }
  }, [pathname]);
  const hasUnread = (convsQ.data ?? []).some((c) => {
    const m = c.last_message;
    if (!m || m.sender_id === profile?.id) return false;
    return new Date(m.created_at).getTime() > lastSeenChat;
  });

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
    setMenuOpen(false);
  }, [pathname]);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const commRef = useRef<HTMLDivElement | null>(null);
  const [communityOpen, setCommunityOpen] = useState(false);
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
      if (!commRef.current?.contains(e.target as Node)) setCommunityOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/", replace: true });
  };

  const avatar =
    profile?.avatar_url ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${profile?.username || "?"}`;
  const displayName = profile?.display_name || profile?.username || "Visitante";
  const visibleMoreNav = moreNav.filter((i) => marketplaceEnabled || i.to !== "/marketplace");
  const visibleCommunityNav = communityNav;
  const visibleMobileExtraNav = mobileExtraNav;
  const allNav = [...primaryNav, ...visibleMobileExtraNav, ...visibleMoreNav];
  const communityActive = visibleCommunityNav.some((i) => pathname.startsWith(i.to));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ── Desktop top bar — estilo Framer Community ─────────── */}
      <DesktopTopBar
        pathname={pathname}
        profile={profile as any}
        isAuthenticated={isAuthenticated}
        authLoading={authLoading}
        isPremium={isPremium}
        hasUnread={hasUnread}
        onSignOut={handleSignOut}
      />

      {/* ── Mobile top bar (minimal, iOS-like) ─────────────────── */}
      <header className="sticky top-0 z-50 flex h-16 items-center gap-2 border-b border-foreground/10 bg-background/75 px-4 backdrop-blur-2xl lg:hidden">
        <Link to="/explore" className="flex items-center">
          <img
            src={vinyIcon.url}
            alt="Viny Club"
            width={32}
            height={32}
            className="h-8 w-8 object-contain"
          />
        </Link>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-label="Buscar"
            onClick={() => setMobileSearchOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-full border border-foreground/10 bg-foreground/[0.05] text-muted-foreground transition active:scale-95"
          >
            <Search className="h-[19px] w-[19px]" />
          </button>
          {isAuthenticated && <NotificationBell />}
          {authLoading ? (
            <div className="ml-1 h-10 w-10 animate-pulse rounded-full bg-surface" />
          ) : isAuthenticated && profile ? (
            <button
              type="button"
              aria-label="Abrir menu"
              onClick={() => setMobileMenuOpen(true)}
              className="relative ml-1 grid h-10 w-10 place-items-center rounded-full transition active:scale-95"
            >
              <FramedAvatar
                userId={profile.id}
                src={profile.avatar_url}
                username={profile.username}
                isPremium={isPremium}
                size={40}
              />
            </button>
          ) : (
            <Link
              to="/auth"
              className="ml-1 rounded-full bg-foreground px-3.5 py-1.5 text-xs font-semibold text-background"
            >
              Entrar
            </Link>
          )}
        </div>
      </header>

      {mobileSearchOpen && (
        <div className="sticky top-12 z-40 border-b border-[color:var(--hairline)] bg-background px-4 py-2 lg:hidden">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              const q = String(fd.get("q") ?? "").trim();
              setMobileSearchOpen(false);
              navigate({ to: "/search", search: { q, tab: "people" as const } });
            }}
            className="relative"
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              name="q"
              type="search"
              placeholder="Buscar pessoas, álbuns, cidades…"
              className="h-10 w-full rounded-full border border-[color:var(--hairline)] bg-surface/60 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-primary/60 focus:outline-none"
            />
          </form>
        </div>
      )}

      {/* ── Main ───────────────────────────────────────────────── */}
      <main
        className={cn(
          "pb-[calc(env(safe-area-inset-bottom)+100px)] lg:pb-0 lg:pt-[60px]",
          // O chat abre em tela cheia no celular; animações de entrada criam um
          // contexto de transform que quebra o posicionamento fixo dessa tela.
          pathname.startsWith("/chat") ? "" : "app-enter ios-push",
        )}
      >
        {children}
        <SiteFooter />
      </main>

      <ProDrawer />
      <ChatDock />

      {/* ── Mobile bottom tab bar (glassmorphism, app-style) ── */}
      <nav
        className="fixed inset-x-0 bottom-2 z-40 flex justify-center px-2 lg:hidden"
        aria-label="Navegação principal"
      >
        <div className="flex w-full max-w-md items-center justify-between gap-1 rounded-[26px] border border-white/[0.08] bg-black/80 p-1.5 shadow-[0_20px_44px_-16px_rgba(0,0,0,0.9)] backdrop-blur-3xl">
          {[
            primaryNav[0],
            primaryNav[1],
            {
              to: "/mini-site",
              label: "Bio mini site",
              shortLabel: "Bio",
              icon: User,
              isNew: true,
            } as NavItem,
          ].map((item) => {
            const active = pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "flex flex-1 flex-col items-center justify-center gap-1 rounded-[22px] py-2 text-[10px] font-semibold tracking-tight transition-all",
                  active
                    ? "bg-white/10 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                    : "text-white/65 active:bg-white/[0.06] active:text-white",
                )}
              >
                <Icon
                  className="h-[21px] w-[21px]"
                  strokeWidth={active ? 2.4 : 1.9}
                />
                <span>{item.shortLabel ?? item.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={cn(
              "flex flex-1 flex-col items-center justify-center gap-1 rounded-[22px] py-2 text-[10px] font-semibold tracking-tight transition-all",
              mobileMenuOpen
                ? "bg-white/10 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                : "text-white/65 active:bg-white/[0.06] active:text-white",
            )}
          >
            <MenuIcon className="h-[21px] w-[21px]" strokeWidth={mobileMenuOpen ? 2.4 : 1.9} />
            <span>Mais</span>
          </button>
        </div>
      </nav>

      {/* ── Mobile menu (bottom sheet em lista, estilo iOS) ── */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="bottom"
          hideClose
          className="max-h-[88vh] overflow-y-auto rounded-t-[28px] border-t border-white/15 bg-background/85 p-0 text-foreground backdrop-blur-2xl"
        >
          <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-foreground/20" />

          <div className="flex items-center justify-between px-5 pt-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Menu
            </span>
            <SheetClose
              aria-label="Fechar menu"
              className="grid h-9 w-9 place-items-center rounded-full border border-foreground/10 bg-foreground/[0.06] text-foreground transition active:scale-95"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2.2} />
            </SheetClose>
          </div>

          {isAuthenticated && profile ? (
            <div className="px-5 pt-3">
              <Link
                to="/profile/$username"
                params={{ username: profile.username }}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3.5 rounded-2xl border border-foreground/10 bg-foreground/[0.04] p-3.5 transition active:scale-[0.99]"
              >
                <FramedAvatar
                  userId={profile.id}
                  src={profile.avatar_url}
                  username={profile.username}
                  isPremium={isPremium}
                  size={54}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-display text-[17px] font-semibold tracking-[-0.02em]">
                    {displayName}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">@{profile.username}</div>
                </div>
              </Link>

              {!isPremium && (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate({ to: "/assinar" });
                  }}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-4 py-3 text-sm font-semibold text-background transition active:scale-[0.98]"
                >
                  <Crown className="h-4 w-4" />
                  Apoie a Viny
                </button>
              )}
            </div>
          ) : (
            <div className="px-5 pt-4">
              <Link
                to="/auth"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center rounded-full bg-foreground px-4 py-3 text-sm font-semibold text-background"
              >
                Entrar / Criar conta
              </Link>
            </div>
          )}

          <div className="space-y-5 px-5 pb-[calc(env(safe-area-inset-bottom)+28px)] pt-5">
            {mobileMenuSections({
              allNav,
              isAuthenticated,
            }).map((section) => (
              <section key={section.title}>
                <div className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  {section.title}
                </div>
                <div className="overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/[0.03]">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = pathname.startsWith(item.to);
                    return (
                      <Link
                        key={item.to + item.label}
                        to={item.to as any}
                        params={item.params as any}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 border-b border-foreground/[0.06] px-3.5 py-3 transition-colors last:border-b-0 active:bg-white/[0.06]",
                          active && "bg-white/[0.05]",
                        )}
                      >
                        <span
                          className={cn(
                            "grid h-9 w-9 shrink-0 place-items-center rounded-full",
                            active
                              ? "bg-foreground text-background"
                              : "bg-foreground/[0.06] text-foreground",
                          )}
                        >
                          <Icon
                            className={cn("h-[17px] w-[17px]", !active && item.tint)}
                            strokeWidth={2}
                          />
                        </span>
                        <span className="flex-1 truncate text-[14px] font-medium">
                          {item.label}
                          {item.tag && (
                            <span className="ml-2 inline-flex rounded-full bg-emerald-400 px-1.5 py-0.5 align-middle text-[8px] font-bold uppercase tracking-wide text-black">
                              {item.tag}
                            </span>
                          )}
                        </span>
                        {active ? (
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}

            <InstallAppMenuCard />

            {isAuthenticated && (
              <button
                onClick={handleSignOut}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MenuItem({
  to,
  params,
  icon: Icon,
  children,
  onClick,
  badge,
}: {
  to: string;
  params?: Record<string, string>;
  icon: typeof User;
  children: ReactNode;
  onClick?: () => void;
  badge?: string;
}) {
  return (
    <Link
      to={to as any}
      params={params as any}
      onClick={onClick}
      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-surface"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="flex-1">{children}</span>
      {badge && (
        <span className="inline-flex items-center rounded-full border border-[color:var(--hairline)] bg-surface/60 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-foreground">
          {badge}
        </span>
      )}
    </Link>
  );
}

type MenuCard = {
  to: string;
  label: string;
  icon: typeof User;
  params?: Record<string, string>;
  tag?: string;
  tint?: string;
};

/** Agrupa a navegação mobile em seções de cards (estilo mega menu iOS). */
function mobileMenuSections({
  allNav,
  isAuthenticated,
}: {
  allNav: NavItem[];
  isAuthenticated: boolean;
}): { title: string; items: MenuCard[] }[] {
  const navegar: MenuCard[] = allNav.map((i) => ({
    to: i.to,
    label: i.label,
    icon: i.icon,
    tag: i.isNew ? "novo" : undefined,
  }));

  const sections: { title: string; items: MenuCard[] }[] = [
    { title: "Navegar", items: navegar },
    {
      title: "Descobrir",
      items: [
        { to: "/ritual", label: "Viny Ritual", icon: CircleDotDashed, tint: "text-primary", tag: "novo" },
        { to: "/insights", label: "Insights", icon: Sparkles, tint: "text-primary" },
        { to: "/colecionadores", label: "Colecionadores", icon: Users },
        { to: "/comunidades", label: "Comunidades", icon: Users },
      ],
    },
  ];

  if (isAuthenticated) {
    sections.push({
      title: "Meu perfil",
      items: [
        {
          to: "/semanal",
          label: "Viny Semanal",
          icon: CalendarDays,
          tint: "text-primary",
          tag: "novo",
        },
        { to: "/conquistas", label: "Conquistas", icon: Award, tint: "text-primary" },
        { to: "/convidar", label: "Convidar", icon: UserPlus },
        { to: "/settings/performance", label: "Desempenho", icon: Activity },
        { to: "/settings/profile", label: "Configurações", icon: Settings },
      ],
    });
  }

  return sections;
}
